import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Button, ErrorMessage, LoadingSpinner, Table, colors, spacing, typography } from '@bafkal/ui-shared';
import { fetchApiKeys, revokeApiKey } from '../apiKeyActions';
import ApiKeyCreateModal from './ApiKeyCreateModal';

export const maskKey = (key) => {
  if (!key) return '';
  if (key.length <= 16) return key;
  const prefix = key.startsWith('pk_live_') ? 'pk_live_...' : key.slice(0, 8) + '...';
  const suffix = key.slice(-4);
  return `${prefix}${suffix}`;
};

export const formatDate = (dateString) => {
  if (!dateString) return '—';
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch (e) {
    return dateString;
  }
};

export function ApiKeysSection() {
  const dispatch = useDispatch();
  const { keys, loading, error, revokeLoading, revokeError } = useSelector(
    (state) => state.apiKeys
  );

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchApiKeys()).catch(() => {});
  }, [dispatch]);

  const handleRevoke = async (id, name) => {
    const confirmed = window.confirm(
      `Are you sure you want to revoke API key "${name || id}"? Any SDK clients using this key will immediately lose access.`
    );
    if (!confirmed) return;

    try {
      await dispatch(revokeApiKey(id));
    } catch (err) {
      // Error captured in Redux revokeError
    }
  };

  const columns = [
    {
      header: 'Name',
      key: 'name',
      render: (name) => (
        <span style={{ fontWeight: 600, color: colors.neutral[900] }}>
          {name || 'Unnamed Key'}
        </span>
      ),
    },
    {
      header: 'Key Mask',
      key: 'key',
      render: (key) => (
        <code
          style={{
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            backgroundColor: colors.neutral[100],
            padding: '2px 8px',
            borderRadius: '4px',
            color: colors.neutral[800],
          }}
        >
          {maskKey(key)}
        </code>
      ),
    },
    {
      header: 'Status',
      key: 'status',
      render: (status) => {
        const isActive = status === 'ACTIVE';
        return (
          <span
            style={{
              padding: '2px 8px',
              borderRadius: '9999px',
              fontSize: '0.75rem',
              fontWeight: 600,
              backgroundColor: isActive ? colors.success[100] : colors.neutral[100],
              color: isActive ? colors.success[800] : colors.neutral[600],
            }}
          >
            {status}
          </span>
        );
      },
    },
    {
      header: 'Created Date',
      key: 'createdAt',
      render: (date) => (
        <span style={{ color: colors.neutral[600], fontSize: typography.sizes.sm }}>
          {formatDate(date)}
        </span>
      ),
    },
    {
      header: 'Actions',
      key: 'actions',
      render: (_, row) =>
        row.status === 'ACTIVE' ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => handleRevoke(row.id, row.name)}
            disabled={revokeLoading}
            style={{
              color: colors.danger[600],
              borderColor: colors.danger[200],
              padding: '2px 10px',
              fontSize: '0.75rem',
            }}
            data-testid={`revoke-key-${row.id}`}
          >
            Revoke
          </Button>
        ) : (
          <span style={{ color: colors.neutral[400], fontSize: '0.75rem', fontStyle: 'italic' }}>
            Revoked
          </span>
        ),
    },
  ];

  return (
    <div style={{ marginTop: spacing.lg }}>
      <Card
        title="API Keys"
        subtitle="API keys are used by the Publisher SDK to authenticate ad serving requests."
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginBottom: spacing.md,
          }}
        >
          <Button
            variant="primary"
            onClick={() => setIsCreateModalOpen(true)}
            data-testid="generate-new-key-button"
          >
            Generate New Key
          </Button>
        </div>

        {error && (
          <ErrorMessage
            message={
              typeof error === 'string' ? error : error.message || 'Failed to load API keys'
            }
          />
        )}

        {revokeError && (
          <ErrorMessage
            message={
              typeof revokeError === 'string'
                ? revokeError
                : revokeError.message || 'Failed to revoke API key'
            }
          />
        )}

        {loading && (!keys || keys.length === 0) ? (
          <div style={{ padding: spacing.xl, display: 'flex', justifyContent: 'center' }}>
            <LoadingSpinner size="lg" />
          </div>
        ) : (
          <Table
            columns={columns}
            data={keys || []}
            keyField="id"
            emptyMessage="No API keys found. Generate a key to get started with the Publisher SDK."
          />
        )}
      </Card>

      <ApiKeyCreateModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />
    </div>
  );
}

export default ApiKeysSection;
